import type React from "react"
import { useEffect, useRef } from "react"
import styled from "styled-components"
import { VippsLoginButtonSVG } from "./VippsLoginButtonSVG"
import type { VippsLoginOptions } from "./types"
import { useVippsLogin } from "./useVippsLogin"

const Button = styled.button`
  padding: 0;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`

export interface VippsLoginButtonProps {
  className?: string
  options: VippsLoginOptions

  width?: number
  height?: number
  border?: string
  disabled?: boolean

  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onVippsLoginResponse: (code: string, redirectUri: string) => void
}

/**
 * Component for Vipps login
 * @param options login options
 * @param options.wellKnownUri Vipps login well known uri
 * @param options.scope Vipps login scope, one or more of
 * [offline_access, offline, openid, address, name, email, phoneNumber,
 * nnin, nin, birthDate, accountNumbers, api_version_2, promotions]
 * @param options.redirectUri Vipps login redirect uri
 * @param options.clientId Vipps login client id
 *
 * @param width width of button in pixels
 * @param height height of button in pixels
 * @param disabled disable button
 *
 * @param onVippsLogin click event handler to handle Vipps login,
 * should be used to exchange code for access token from backend.
 * @example
 * function handleVippsLogin(code: string, redirectUri: string) {
 *   vippsLoginMutation({
 *     variables: {
 *       code,
 *       redirectUri,
 *     },
 *   })
 * }
 * // With vippsLoginMutation being something like this:
 * const [vippsLoginMutation] = useMutation(VIPPS_LOGIN_MUTATION, {
 *   onCompleted(data) {
 *     if (!data) return
 *     const token = data.vippsLoginMutation.token
 *     setLoginToken(token)
 *     client.resetStore()
 *   },
 * })
 * // Backend example:
 * @see: https://github.com/Ur-Solutions/nuumi-backend/blob/develop/authentication/schema.py
 */
export const VippsLoginButton: React.FC<VippsLoginButtonProps> = ({
  className,
  options,

  width = 210,
  height,
  disabled = false,

  onClick,
  onVippsLoginResponse,
}) => {
  const hasFiredVippsLoginResponse = useRef(false)
  const { handleVippsLogin, ready } = useVippsLogin(options)

  useEffect(() => {
    if (hasFiredVippsLoginResponse.current) return

    const queryParameters = new URLSearchParams(window.location.search)
    const code = queryParameters.get("code")
    if (!code) return

    onVippsLoginResponse(code, options.redirectUri)
    hasFiredVippsLoginResponse.current = true
  }, [onVippsLoginResponse, options.redirectUri])

  function handleClick(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    onClick?.(evt)
    if (evt.defaultPrevented) return

    handleVippsLogin()
  }

  return (
    <Button
      className={className}
      disabled={disabled || !ready}
      onClick={handleClick}
    >
      <VippsLoginButtonSVG width={width} height={height} />
    </Button>
  )
}
